"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Award, CircleCheck as CheckCircle2, Clock, Flame, CircleQuestionMark as HelpCircle, PhoneCall, ShieldCheck, Star, Wrench } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const trustBadges = [{
  id: 1,
  title: "Factory-Trained Rinnai Techs",
  desc: "Direct certified training on Rinnai SENSEI, RU, and RL series.",
  iconName: "Award"
}, {
  id: 2,
  title: "15+ Years Experience",
  desc: "Over 1,400+ Rinnai tankless installations and emergency repairs.",
  iconName: "Clock"
}, {
  id: 3,
  title: "OEM Replacement Parts",
  desc: "Fully stocked service vans with genuine Rinnai sensors and valves.",
  iconName: "ShieldCheck"
}, {
  id: 4,
  title: "Same-Day Diagnostics",
  desc: "Rapid response across Jonesboro and surrounding South Metro GA.",
  iconName: "Flame"
}];
const faqs = [{
  id: "item-1",
  question: "What do common Rinnai error codes (Code 10, 11, or 12) mean?",
  answer: "Rinnai error codes indicate specific component issues. Code 10 means a problem with the flame sensor or vent blockage; Code 11 is an ignition failure (often a gas supply or igniter issue); and Code 12 signals flame failure during operation. Our certified technicians carry factory diagnostic tools to pinpoint the exact sensor or gas valve fault immediately."
}, {
  id: "item-2",
  question: "How often does my Rinnai tankless water heater need flushing?",
  answer: "In Georgia's municipal and well water conditions, Rinnai recommends flushing the heat exchanger every 12 to 18 months with a specialized non-toxic descaling solution. Regular descaling clears mineral build-up, maintains 97% thermal efficiency, and protects your manufacturer warranty."
}, {
  id: "item-3",
  question: "Can you convert an old tank water heater to a high-efficiency Rinnai system?",
  answer: "Yes. We specialize in tank-to-tankless retrofits throughout Jonesboro. We evaluate your gas line diameter (ensuring proper BTU supply), water line routing, and direct-vent termination to ensure seamless endless hot water without structural disruption."
}, {
  id: "item-4",
  question: "Why is my Rinnai water heater running lukewarm or fluctuating in temperature?",
  answer: "Temperature fluctuation (often known as the 'cold water sandwich') can result from a clogged inlet water filter, improper minimum flow rate, failing flow sensor, or an uncalibrated thermistor. We inspect and recalibrate the system to restore steady, precise water temperature."
}, {
  id: "item-5",
  question: "Do you offer emergency 24/7 service for Rinnai water heaters?",
  answer: "Yes. We provide 24/7 emergency dispatch for water leaks, total hot water loss, and gas lockout codes on all Rinnai residential and commercial models in Jonesboro and neighboring areas."
}];
const testimonials = [{
  id: 1,
  name: "Marcus Sterling",
  role: "Jonesboro Homeowner",
  initials: "MS",
  rating: 5,
  quote: "Our Rinnai threw an error 11 code on a Sunday morning. Jonesboro Emergency Plumber had a certified tech here in 45 minutes with the exact factory igniter part. Hot water was back on before noon."
}, {
  id: 2,
  name: "Evelyn Carter",
  role: "Clayton County Resident",
  initials: "EC",
  rating: 5,
  quote: "They completed a clean conversion from our 14-year-old leaking tank to a Rinnai SENSEI model. Gas piping, concentric venting, and permits were all handled impeccably."
}];
export default function RinnaiTrust() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="rinnaifaqandtrust" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Intro */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-semibold text-xs uppercase tracking-wider">
            Rinnai Certified Specialists
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
            Rinnai Tankless Expertise & Frequently Asked Questions
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg">
            Certified installation, rapid diagnostics, and genuine OEM parts backed by over 15 years of dedicated Rinnai water heating experience in Jonesboro, GA.
          </p>
        </div>

        {/* Trust Badges Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {trustBadges.map((badge, index) => {
          return <motion.div key={badge.id} data-index={index} initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.35,
            delay: index * 0.1
          }} className="h-full">
                <Card className="h-full border border-border shadow-xs hover:shadow-md transition-shadow bg-card text-card-foreground">
                  <CardHeader className="pb-2">
                    <div className="p-3 bg-primary/10 text-primary w-fit rounded-lg mb-3">
                      {badge.iconName === "Award" && <Award className="size-6 text-primary" />}
                      {badge.iconName === "Clock" && <Clock className="size-6 text-primary" />}
                      {badge.iconName === "ShieldCheck" && <ShieldCheck className="size-6 text-primary" />}
                      {badge.iconName === "Flame" && <Flame className="size-6 text-primary" />}
                    </div>
                    <CardTitle className="text-lg font-bold">{badge.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {badge.desc}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Main Content Layout: FAQ Accordion + Testimonials / CTA Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* FAQ Column (7 cols) */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-6">
              <HelpCircle className="size-5 text-primary" />
              <h3 className="font-default text-2xl font-bold tracking-tight">
                Rinnai Troubleshooting & Services FAQ
              </h3>
            </div>

            <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
              {faqs.map((faq, index) => <AccordionItem key={faq.id} value={faq.id} data-index={index} className="border border-border rounded-lg mb-3 px-4 bg-card">
                  <AccordionTrigger className="text-base font-semibold hover:no-underline text-foreground py-4 text-left">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed pb-4 pt-1">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {/* Testimonials & Direct Action Column (5 cols) */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            {/* Direct Tech Assistance Banner */}
            <Card className="border border-primary/20 bg-primary/5 text-card-foreground shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Wrench className="size-5 text-primary" />
                  <span className="text-xs font-bold uppercase tracking-wider text-primary">
                    Fast Local Dispatch
                  </span>
                </div>
                <CardTitle className="text-xl font-bold mt-1">
                  Need Immediate Rinnai Repair?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Do not stay without hot water. Our licensed emergency plumbers are standing by 24/7 across Jonesboro with factory diagnostics.
                </p>
                <div className="space-y-2 pt-1 text-sm font-medium text-foreground">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary" />
                    <span>Same-day arrival for emergency codes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary" />
                    <span>Upfront, flat-rate quote before repair starts</span>
                  </div>
                </div>
              </CardContent>
              <div className="px-6 pb-6 pt-0">
                <Button asChild size="lg" className="w-full gap-2 shadow-sm font-semibold">
                  <Link to="/contact/">
                    <PhoneCall className="size-4" />
                    <span>Request Emergency Rinnai Service</span>
                  </Link>
                </Button>
              </div>
            </Card>

            <Separator />

            {/* Testimonials Section */}
            <div className="space-y-4">
              <h4 className="text-base font-bold text-foreground">
                Recent Rinnai Service Reviews
              </h4>

              {testimonials.map((test, index) => <Card key={test.id} data-index={index} className="border border-border bg-card text-card-foreground shadow-xs p-5">
                  <div className="flex items-center gap-1 text-amber-500 mb-3">
                    {Array.from({
                  length: test.rating
                }).map((_, starIdx) => <Star key={starIdx} className="size-4 fill-current text-accent" />)}
                  </div>
                  <p className="text-sm text-foreground/90 italic mb-4">
                    "{test.quote}"
                  </p>
                  <div className="flex items-center gap-3">
                    <SafeHydrate><Avatar className="size-9 border border-border">
                      <AvatarFallback className="bg-primary text-primary-foreground font-semibold text-xs">
                        {test.initials}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    <div>
                      <p className="text-sm font-semibold text-foreground leading-tight">
                        {test.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {test.role}
                      </p>
                    </div>
                  </div>
                </Card>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}